import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Ban, RefreshCcw } from "lucide-react";
import toast from "react-hot-toast";
import { RootDispatch, RootState } from "../../../store";
import {
  cancelReplaceReturnRequest,
  getReplaceReturnRequests,
} from "../../../store/slices/Factory/materialReturnSlice";
import {
  FloorMaterialReturnStatus,
  IFloorMaterialReturnRequest,
} from "../../../models/factory/floor/floorReturnRequest.model";
import { formatToDDMMYYYY } from "../../../utils/formatDateUtils";
import PaginationControls from "../../../utils/PaginationControls";
import ConfirmationModal from "../../../components/Assets/ConfirmationModal";
import FloorMaterialReturnWorkflowModal from "./FloorMaterialReturnWorkflowModal";

type ReturnRequestStatusFilter =
  | ""
  | "pending_store_review"
  | "completed"
  | "cancelled";

const statusFilters: { label: string; value: ReturnRequestStatusFilter }[] = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending_store_review" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled / Rejected", value: "cancelled" },
];

export default function FloorMaterialReturnRequestsPage() {
  const dispatch: RootDispatch = useDispatch();
  const role = useSelector((state: RootState) => state.auth.role);
  const canManageRequests = ["store", "factory", "admin"].includes(role || "");
  const requests = useSelector(
    (state: RootState) => state.materialReturn.returnRequests,
  );
  const pagination = useSelector(
    (state: RootState) => state.materialReturn.pagination,
  );
  const loading = useSelector(
    (state: RootState) =>
      state.materialReturn.loading.getReplaceReturnRequestsLoading,
  );
  const cancelLoading = useSelector(
    (state: RootState) =>
      state.materialReturn.loading.cancelReplaceReturnRequestLoading,
  );
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] =
    useState<ReturnRequestStatusFilter>("");
  const [requestToCancel, setRequestToCancel] =
    useState<IFloorMaterialReturnRequest | null>(null);
  const [workflowRequest, setWorkflowRequest] =
    useState<IFloorMaterialReturnRequest | null>(null);

  useEffect(() => {
    dispatch(
      getReplaceReturnRequests({
        page,
        limit: 12,
        ...(statusFilter ? { status: statusFilter } : {}),
      }),
    );
  }, [dispatch, page, statusFilter]);

  const changeStatusFilter = (value: ReturnRequestStatusFilter) => {
    setStatusFilter(value);
    setPage(1);
  };

  const confirmCancel = async () => {
    if (!requestToCancel) return;
    try {
      await dispatch(
        cancelReplaceReturnRequest({ requestId: requestToCancel._id }),
      ).unwrap();
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Could not cancel the return request.",
      );
      return;
    }

    setRequestToCancel(null);
    toast.success("Return request cancelled.");
    try {
      await dispatch(
        getReplaceReturnRequests({
          page,
          limit: 12,
          ...(statusFilter ? { status: statusFilter } : {}),
        }),
      ).unwrap();
    } catch {
      toast.error("Request cancelled, but the list could not be refreshed.");
    }
  };

  const cancelButton = (request: IFloorMaterialReturnRequest) =>
    request.status === "pending_store_review" ? (
      <button
        type="button"
        title="Cancel return request"
        aria-label={`Cancel return request for ${request.rawMaterialId?.name || "material"}`}
        onClick={() => setRequestToCancel(request)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-rose-200 text-rose-600 transition hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-200"
      >
        <Ban className="h-4 w-4" />
      </button>
    ) : null;

  return (
    <>
      <div
        className="mb-5 mt-4 ml-4 flex flex-wrap gap-2"
        role="group"
        aria-label="Filter return requests by status"
      >
        {statusFilters.map((filter) => (
          <button
            key={filter.label}
            type="button"
            aria-pressed={statusFilter === filter.value}
            onClick={() => changeStatusFilter(filter.value)}
            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 ${
              statusFilter === filter.value
                ? "border-indigo-600 bg-indigo-600 text-white"
                : "border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50"
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-500">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
          <span className="text-sm">Loading return requests...</span>
        </div>
      ) : requests.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-14 text-center">
          <RefreshCcw className="mx-auto h-8 w-8 text-gray-400" />
          <h2 className="mt-3 text-base font-semibold text-gray-800">
            No return requests yet
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Material return and replacement requests will appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-lg border border-gray-200 bg-white min-[1000px]:block">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500">
                <tr>
                  <th className="px-5 py-3">Batch</th>
                  <th className="px-5 py-3">Material</th>
                  <th className="px-5 py-3 text-right">Quantity</th>
                  <th className="px-5 py-3 text-right">Received</th>
                  <th className="px-5 py-3">Requested By</th>
                  <th className="px-5 py-3">Created</th>
                  <th className="px-5 py-3">Reason</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {requests.map((request: IFloorMaterialReturnRequest) => (
                  <tr key={request._id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-5 py-4 font-medium text-gray-900">
                      #{request.floorRequestId?.batchNo ?? "N/A"}
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-900">
                        {request.rawMaterialId?.name || "Unknown material"}
                      </div>
                      {request.rawMaterialId?.sku && (
                        <div className="mt-0.5 text-xs text-gray-500">
                          SKU {request.rawMaterialId.sku}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-right font-semibold text-gray-900">
                      {request.quantity} {request.rawMaterialId?.unit}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-right text-gray-600">
                      {request.receivedQuantity} {request.rawMaterialId?.unit}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-gray-700">
                      {request.createdBy?.username || "N/A"}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-gray-600">
                      {formatToDDMMYYYY(request.createdAt)}
                    </td>
                    <td className="max-w-xs px-5 py-4 text-gray-600">
                      <span className="block truncate" title={request.reason}>
                        {request.reason || "N/A"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-5 py-4">
                      <ReturnRequestStatus status={request.status} />
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {canManageRequests && (
                          <button
                            type="button"
                            onClick={() => setWorkflowRequest(request)}
                            className="rounded-md border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                          >
                            Manage
                          </button>
                        )}
                        {cancelButton(request)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 min-[1000px]:hidden">
            {requests.map((request: IFloorMaterialReturnRequest) => (
              <article
                key={request._id}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase text-gray-500">
                      Batch #{request.floorRequestId?.batchNo ?? "N/A"}
                    </p>
                    <h2 className="mt-1 truncate font-semibold text-gray-900">
                      {request.rawMaterialId?.name || "Unknown material"}
                    </h2>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <ReturnRequestStatus status={request.status} />
                    {canManageRequests && (
                      <button
                        type="button"
                        onClick={() => setWorkflowRequest(request)}
                        className="rounded-md border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                      >
                        Manage
                      </button>
                    )}
                    {cancelButton(request)}
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-gray-100 pt-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Quantity</p>
                    <p className="mt-0.5 font-semibold text-gray-900">
                      {request.quantity} {request.rawMaterialId?.unit}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Received</p>
                    <p className="mt-0.5 font-medium text-gray-800">
                      {request.receivedQuantity} {request.rawMaterialId?.unit}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Requested By</p>
                    <p className="mt-0.5 truncate font-medium text-gray-800">
                      {request.createdBy?.username || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Created</p>
                    <p className="mt-0.5 font-medium text-gray-800">
                      {formatToDDMMYYYY(request.createdAt)}
                    </p>
                  </div>
                  {request.reason && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500">Reason</p>
                      <p className="mt-0.5 break-words text-gray-800">
                        {request.reason}
                      </p>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {(loading || requests.length > 0) && (
        <PaginationControls
          currentPage={page}
          onPageChange={setPage}
          totalPages={pagination?.totalPages || 0}
          isLoading={loading}
          totalItems={pagination?.totalCount}
        />
      )}
      <ConfirmationModal
        isOpen={!!requestToCancel}
        title="Cancel Return Request?"
        subTitle={requestToCancel?.rawMaterialId?.name || "Material request"}
        description={`Batch #${requestToCancel?.floorRequestId?.batchNo ?? "N/A"} · ${requestToCancel?.quantity ?? 0} ${requestToCancel?.rawMaterialId?.unit || ""}. This request will be cancelled.`}
        confirmText="Cancel Request"
        cancelText="Keep Request"
        danger
        loading={cancelLoading}
        onConfirm={confirmCancel}
        onClose={() => setRequestToCancel(null)}
      />
      {workflowRequest && canManageRequests && (
        <FloorMaterialReturnWorkflowModal
          request={workflowRequest}
          page={page}
          statusFilter={statusFilter}
          onClose={() => setWorkflowRequest(null)}
          onRequestUpdated={setWorkflowRequest}
        />
      )}
    </>
  );
}

function ReturnRequestStatus({
  status,
}: {
  status: FloorMaterialReturnStatus;
}) {
  const styles: Record<FloorMaterialReturnStatus, string> = {
    pending_store_review: "border-amber-200 bg-amber-50 text-amber-800",
    rejected: "border-rose-200 bg-rose-50 text-rose-800",
    cancelled: "border-gray-300 bg-gray-100 text-gray-700",
    accepted: "border-sky-200 bg-sky-50 text-sky-800",
    in_progress: "border-indigo-200 bg-indigo-50 text-indigo-800",
    completed: "border-emerald-200 bg-emerald-50 text-emerald-800",
  };

  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${styles[status]}`}
    >
      {status.replaceAll("_", " ")}
    </span>
  );
}
